import React, { useState, useEffect, useContext } from 'react';
import api from '../api';
import { AuthContext } from '../AuthContext';
import RedactModal from './RedactModal';

const HOAFilesRequest = () => {
  const { user } = useContext(AuthContext);
  const [grants, setGrants] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [documentSearch, setDocumentSearch] = useState('');
  const [properties, setProperties] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showRedactModal, setShowRedactModal] = useState(false);
  const [redactDocId, setRedactDocId] = useState(null);
  const [redactDocTitle, setRedactDocTitle] = useState('');
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [consentStatus, setConsentStatus] = useState(null);
  const [previewDocUrl, setPreviewDocUrl] = useState(null);
  const [timezone, setTimezone] = useState('America/Chicago');
  
  const [createData, setCreateData] = useState({
    recipient_email: '',
    recipient_user_id: '',
    days: 7,
    password_plain: '',
    require_privacy_policy: true,
    require_e_consent_for_guest: true
  });

  const isBoard = ['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 'platform_admin'].includes(user?.role?.toLowerCase());

  useEffect(() => {
    if (isBoard) {
      loadGrants();
      loadDocuments();
      loadProperties();
    }
  }, []);

  const loadGrants = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/file-grants?hoa_code=${user.hoa_id}`);
      setGrants(response.data.grants || []);
      setTimezone(response.data.timezone || 'America/Chicago');
    } catch (error) {
      console.error('Error loading grants:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      console.log('[GRANT] Loading all documents...');
      
      // Use the comprehensive all-documents endpoint
      const response = await api.get('/api/hoa-files-pro/all-documents');
      
      console.log('[GRANT] Response:', response.data);
      console.log('[GRANT] Breakdown:', response.data.breakdown);
      
      const docs = response.data.documents || [];
      
      console.log(`[GRANT] Loaded ${docs.length} total documents`);
      console.log('[GRANT] Document titles:', docs.map(d => d.title).slice(0, 10));
      
      setDocuments(docs);
      setFilteredDocuments(docs);
    } catch (error) {
      console.error('[GRANT] Error loading all documents:', error);
      console.error('[GRANT] Error response:', error.response?.data);
      
      // Fallback to just HOA documents
      try {
        console.log('[GRANT] Falling back to hoa-documents only...');
        const response = await api.get('/api/hoa-files-pro/hoa-documents');
        const docs = response.data.documents || [];
        console.log(`[GRANT] Fallback loaded ${docs.length} documents`);
        setDocuments(docs);
        setFilteredDocuments(docs);
      } catch (fallbackError) {
        console.error('[GRANT] Fallback also failed:', fallbackError);
      }
    }
  };

  const loadProperties = async () => {
    try {
      const response = await api.get('/api/hoa-files-pro/properties');
      setProperties(response.data.properties || []);
    } catch (error) {
      console.error('Error loading properties:', error);
    }
  };

  const handleSelectAllFromFolder = (propertyAddress) => {
    if (!propertyAddress) return;
    
    // Find all documents tagged to this property address or its homeowners
    const property = properties.find(p => p.address === propertyAddress);
    if (!property) return;
    
    const homeownerIds = property.homeowners?.map(h => h.id) || [];
    
    const folderDocs = documents.filter(doc => {
      // Check if tagged to any homeowner in this property
      const taggedIds = doc.tagged_homeowners || [];
      return taggedIds.some(id => homeownerIds.includes(id));
    });
    
    const folderDocIds = folderDocs.map(d => d.id);
    const newSelections = [...new Set([...selectedDocs, ...folderDocIds])];
    setSelectedDocs(newSelections);
    
    alert(`Added ${folderDocIds.length} document(s) from ${propertyAddress} folder.\nTotal selected: ${newSelections.length}`);
  };

  const handleDocumentSearch = (searchTerm) => {
    setDocumentSearch(searchTerm);
    
    if (!searchTerm.trim()) {
      setFilteredDocuments(documents);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = documents.filter(doc => 
      doc.title?.toLowerCase().includes(term) ||
      doc.category?.toLowerCase().includes(term) ||
      doc.subcategory?.toLowerCase().includes(term) ||
      doc.description?.toLowerCase().includes(term)
    );
    
    setFilteredDocuments(filtered);
  };

  const handleSelectAllFiltered = () => {
    // Add all filtered documents to selection (accumulative)
    const filteredIds = filteredDocuments.map(d => d.id);
    const newSelections = [...new Set([...selectedDocs, ...filteredIds])];
    setSelectedDocs(newSelections);
  };

  const handleDeselectAllFiltered = () => {
    // Remove all filtered documents from selection
    const filteredIds = new Set(filteredDocuments.map(d => d.id));
    const newSelections = selectedDocs.filter(id => !filteredIds.has(id));
    setSelectedDocs(newSelections);
  };

  const handleClearAllSelections = () => {
    setSelectedDocs([]);
  };

  const loadGrantDetails = async (grantId) => {
    try {
      const [detailsRes, consentRes] = await Promise.all([
        api.get(`/api/file-grants/${grantId}/details`),
        api.get(`/api/file-grants/${grantId}/recipient-consent`)
      ]);
      
      setSelectedGrant(detailsRes.data);
      setConsentStatus(consentRes.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error loading grant details:', error);
      alert('Failed to load grant details');
    }
  };

  const toggleDocSelection = (docId) => {
    if (selectedDocs.includes(docId)) {
      setSelectedDocs(selectedDocs.filter(id => id !== docId));
    } else {
      setSelectedDocs([...selectedDocs, docId]);
    }
  };

  const handleCreateDraftGrant = async () => {
    if (selectedDocs.length === 0) {
      alert('Please select at least one document');
      return;
    }

    if (!createData.password_plain) {
      alert('Please set a password for the grant');
      return;
    }

    if (!createData.recipient_email && !createData.recipient_user_id) {
      alert('Please enter a recipient email or select a user');
      return;
    }

    try {
      const payload = {
        hoa_code: user.hoa_id,
        doc_ids: selectedDocs,
        ...createData
      };

      const response = await api.post('/api/file-grants', payload);
      
      alert(`Draft grant created! Grant ID: ${response.data.id}\n\nPlease review and redact documents before activating.`);
      
      // Reset form
      setShowCreateModal(false);
      setSelectedDocs([]);
      setDocumentSearch('');
      setFilteredDocuments(documents);
      setCreateData({
        recipient_email: '',
        recipient_user_id: '',
        days: 7,
        password_plain: '',
        require_privacy_policy: true,
        require_e_consent_for_guest: true
      });
      
      loadGrants();
    } catch (error) {
      console.error('Error creating grant:', error);
      alert('Failed to create grant: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleActivateGrant = async (grantId) => {
    if (!window.confirm('Are you sure you want to activate this grant? The recipient will be able to access the documents and the countdown timer will start.')) {
      return;
    }

    try {
      const response = await api.post(`/api/file-grants/${grantId}/activate`);
      alert(`Grant activated successfully!\n\nAccess starts: ${new Date(response.data.starts_at).toLocaleString()}\nExpires: ${new Date(response.data.expires_at).toLocaleString()}\n\nYou can now send the access email to the recipient.`);
      loadGrants();
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error activating grant:', error);
      alert('Failed to activate grant: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSendEmail = (grant) => {
    const mailtoBody = `You have been granted temporary access to view selected HOA documents.\n\nGrant ID: ${grant.id}\nPassword: [You set this]\nAccess Duration: ${grant.days_duration || 7} days\n\nThis access is view-only with download disabled.\n\nPlease contact the board for the grant password.`;
    
    window.location.href = `mailto:${grant.recipient_email}?subject=Secure Document Access&body=${encodeURIComponent(mailtoBody)}`;
  };

  const handlePreviewDocument = async (docId) => {
    try {
      // Use ensure-pdf to convert if needed, then open in modal
      const response = await api.post('/api/preview/ensure-pdf', {
        hoa_code: user.hoa_id,
        doc_id: docId,
        grant_id: selectedGrant?.id,
        days: 1
      });
      
      const tempId = response.data.temp_doc_id;
      const url = `/api/preview/stream?temp_doc_id=${tempId}`;
      setPreviewDocUrl(url);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error previewing document:', error);
      alert('Failed to preview document: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRevokeGrant = async (grantId) => {
    if (!window.confirm('Are you sure you want to revoke this grant? Users will no longer be able to access the documents.')) {
      return;
    }

    try {
      await api.post(`/api/file-grants/${grantId}/revoke`);
      alert('Grant revoked successfully');
      loadGrants();
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error revoking grant:', error);
      alert('Failed to revoke grant: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRedact = async (docId, docTitle) => {
    setRedactDocId(docId);
    setRedactDocTitle(docTitle || 'Document');
    setShowRedactModal(true);
  };

  const handleRedacted = (tempDocId) => {
    // Refresh grant details to show new temp doc
    if (selectedGrant) {
      loadGrantDetails(selectedGrant.id);
    }
    loadDocuments();
    alert(`✅ Document redacted! Temp document ID: ${tempDocId}\n\nThe redacted document has been added to this grant and will auto-expire with the grant.`);
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      expired: 'bg-gray-100 text-gray-800',
      revoked: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (!isBoard) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-sm">
        <p className="text-gray-600">Board member access required to manage file requests.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-gray-800">HOA Files Request</h2>
            <span className="px-3 py-1 bg-purple-100 text-purple-600 text-xs font-semibold rounded-full">
              In Beta
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">Secure, time-limited document sharing with redaction support</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
        >
          <span>+</span> Create New Grant
        </button>
      </div>

      {/* Grants List */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Document Access Grants</h3>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : grants.length === 0 ? (
          <p className="text-gray-500">No grants created yet</p>
        ) : (
          <div className="space-y-3">
            {grants.map(grant => (
              <div key={grant.id} className="border rounded-lg p-4 hover:bg-gray-50 transition">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(grant.status)}`}>
                        {grant.status.toUpperCase()}
                      </span>
                      {grant.status === 'active' && grant.expires_at_tz && (
                        <span className="text-xs text-gray-600">
                          ⏱ Expires: {grant.expires_at_tz.local}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 space-y-1">
                      <p><strong>Recipient:</strong> {grant.recipient_email || grant.recipient_user_id}</p>
                      <p><strong>Documents:</strong> {grant.doc_titles?.join(', ') || `${grant.doc_ids?.length} document(s)`}</p>
                      <p><strong>Created:</strong> {grant.created_at_tz?.local || 'N/A'}</p>
                      {grant.status === 'active' && (
                        <p><strong>Activated:</strong> {grant.activated_at_tz?.local || 'N/A'}</p>
                      )}
                      <p><strong>Access Count:</strong> {grant.access_count || 0} time(s)</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => loadGrantDetails(grant.id)}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      View Details
                    </button>
                    {grant.status === 'draft' && (
                      <button
                        onClick={() => loadGrantDetails(grant.id)}
                        className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        Review & Activate
                      </button>
                    )}
                    {grant.status === 'active' && (
                      <>
                        <button
                          onClick={() => handleSendEmail(grant)}
                          className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                        >
                          Send Email
                        </button>
                        <button
                          onClick={() => handleRevokeGrant(grant.id)}
                          className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Revoke
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Grant Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Create Document Grant (Draft)</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setDocumentSearch('');
                  setFilteredDocuments(documents);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                ℹ️ Grant will be created in <strong>DRAFT</strong> status. You can review, preview, and redact documents before activating access.
              </p>
            </div>

            <div className="space-y-4">
              {/* Recipient */}
              <div>
                <label className="block text-sm font-medium mb-2">Recipient Email *</label>
                <input
                  type="email"
                  value={createData.recipient_email}
                  onChange={(e) => setCreateData({...createData, recipient_email: e.target.value})}
                  placeholder="attorney@example.com or user@homeowner.com"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium mb-2">Access Duration (starts when activated)</label>
                <select
                  value={createData.days}
                  onChange={(e) => setCreateData({...createData, days: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value={3}>3 days</option>
                  <option value={5}>5 days</option>
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium mb-2">Grant Password *</label>
                <input
                  type="password"
                  value={createData.password_plain}
                  onChange={(e) => setCreateData({...createData, password_plain: e.target.value})}
                  placeholder="Set a secure password"
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Recipient will need this password to view documents</p>
              </div>

              {/* Document Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">
                    Select Documents * ({selectedDocs.length} selected of {documents.length} total)
                  </label>
                  <button
                    onClick={handleClearAllSelections}
                    disabled={selectedDocs.length === 0}
                    className="text-xs px-2 py-1 border border-red-300 text-red-600 rounded hover:bg-red-50 disabled:opacity-50"
                  >
                    Clear All
                  </button>
                </div>

                {/* Folder Selection */}
                <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <label className="block text-sm font-medium mb-2">Quick Select: All Files from Homeowner Folder</label>
                  <select
                    onChange={(e) => handleSelectAllFromFolder(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    defaultValue=""
                  >
                    <option value="">-- Select a property/homeowner folder --</option>
                    {properties.map(prop => (
                      <option key={prop.address} value={prop.address}>
                        {prop.address} ({prop.homeowners?.length || 0} homeowner(s))
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-600 mt-1">💡 Select a folder to add all that homeowner's documents at once</p>
                </div>

                {/* Search/Filter */}
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={documentSearch}
                    onChange={(e) => handleDocumentSearch(e.target.value)}
                    placeholder="Search by title, category, or description..."
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                  />
                  <button
                    onClick={handleSelectAllFiltered}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium whitespace-nowrap"
                  >
                    + Select All ({filteredDocuments.length})
                  </button>
                  <button
                    onClick={handleDeselectAllFiltered}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium whitespace-nowrap"
                  >
                    − Deselect All
                  </button>
                </div>

                {documentSearch && (
                  <div className="mb-2 text-sm text-gray-600">
                    Showing {filteredDocuments.length} of {documents.length} documents
                    {filteredDocuments.length === 0 && (
                      <span className="text-red-600 ml-2">No documents match your search</span>
                    )}
                  </div>
                )}

                <div className="border rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
                  {filteredDocuments.length === 0 && !documentSearch && (
                    <p className="text-gray-500 text-sm">No documents available</p>
                  )}
                  {filteredDocuments.length === 0 && documentSearch && (
                    <p className="text-gray-500 text-sm">No documents match "{documentSearch}"</p>
                  )}
                  {filteredDocuments.map(doc => (
                    <label key={doc.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDocs.includes(doc.id)}
                        onChange={() => toggleDocSelection(doc.id)}
                        className="rounded"
                      />
                      <div className="flex-1">
                        <span className="font-medium">{doc.title}</span>
                        <div className="flex gap-2 text-xs text-gray-500">
                          <span className="px-1.5 py-0.5 bg-gray-100 rounded">{doc.category}</span>
                          {doc.subcategory && (
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">{doc.subcategory}</span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Tip: Use search to filter documents by type (e.g., "legal", "financial"), then click "Select All" to add them. 
                  You can search multiple times and keep adding documents.
                </p>
              </div>

              {/* Options */}
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={createData.require_privacy_policy}
                    onChange={(e) => setCreateData({...createData, require_privacy_policy: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Require Privacy Policy acceptance</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={createData.require_e_consent_for_guest}
                    onChange={(e) => setCreateData({...createData, require_e_consent_for_guest: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Require E-Consent for guest access</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setDocumentSearch('');
                    setFilteredDocuments(documents);
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateDraftGrant}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Draft Grant
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grant Details Modal */}
      {showDetailsModal && selectedGrant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Grant Details</h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedGrant(null);
                  setConsentStatus(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded font-medium ${getStatusBadge(selectedGrant.status)}`}>
                  {selectedGrant.status.toUpperCase()}
                </span>
                <span className="text-sm text-gray-600">Grant ID: {selectedGrant.id}</span>
              </div>

              {/* Recipient & Consent Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Recipient Information</h4>
                <div className="text-sm space-y-1">
                  <p><strong>Name/Email:</strong> {consentStatus?.recipient_name || selectedGrant.recipient_email}</p>
                  <p><strong>Privacy Policy:</strong> {consentStatus?.privacy_policy_signed ? 
                    <span className="text-green-600">✓ Signed</span> : 
                    <span className="text-red-600">✗ Not Signed</span>}
                  </p>
                  <p><strong>E-Consent:</strong> {consentStatus?.e_consent_signed ? 
                    <span className="text-green-600">✓ Signed</span> : 
                    <span className="text-red-600">✗ Not Signed</span>}
                  </p>
                  {!consentStatus?.user_found && (
                    <p className="text-yellow-700 mt-2">⚠️ External guest - consent will be required on first access</p>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Timeline (Timezone: {timezone})</h4>
                <div className="text-sm space-y-1">
                  <p><strong>Created:</strong> {selectedGrant.created_at_tz?.local || 'N/A'}</p>
                  {selectedGrant.activated_at_tz && (
                    <p><strong>Activated:</strong> {selectedGrant.activated_at_tz.local}</p>
                  )}
                  {selectedGrant.expires_at_tz && (
                    <p><strong>Expires:</strong> {selectedGrant.expires_at_tz.local}</p>
                  )}
                  <p><strong>Duration:</strong> {selectedGrant.days_duration || 7} days</p>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h4 className="font-semibold mb-2">Documents ({selectedGrant.documents?.length || 0})</h4>
                <div className="space-y-2">
                  {selectedGrant.documents?.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between border rounded p-2 hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{doc.title}</p>
                        <p className="text-xs text-gray-500">{doc.category} {doc.subcategory && `• ${doc.subcategory}`}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePreviewDocument(doc.id)}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          Preview
                        </button>
                        {selectedGrant.status === 'draft' && (
                          <button
                            onClick={() => handleRedact(doc.id, doc.title)}
                            className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                          >
                            🔒 Redact <span className="text-yellow-300 font-bold">BETA</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Access Log */}
              {selectedGrant.access_log && selectedGrant.access_log.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Access Log</h4>
                  <div className="text-sm space-y-1 max-h-32 overflow-y-auto">
                    {selectedGrant.access_log.map((log, idx) => (
                      <p key={idx} className="text-gray-700">
                        {new Date(log.at).toLocaleString()} - {log.user} viewed {log.doc_id}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                {selectedGrant.status === 'draft' && (
                  <button
                    onClick={() => handleActivateGrant(selectedGrant.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Activate Grant
                  </button>
                )}
                {selectedGrant.status === 'active' && (
                  <>
                    <button
                      onClick={() => handleSendEmail(selectedGrant)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Send Email
                    </button>
                    <button
                      onClick={() => handleRevokeGrant(selectedGrant.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Revoke Access
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedGrant(null);
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[90vw] h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">Document Preview</h3>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewDocUrl(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={previewDocUrl}
                className="w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      )}

      {/* Redaction Modal */}
      {showRedactModal && (
        <RedactModal
          hoaCode={user.hoa_id}
          sourceDocId={redactDocId}
          grantId={selectedGrant?.id}
          defaultDays={30}
          onClose={() => {
            setShowRedactModal(false);
            setRedactDocId(null);
            setRedactDocTitle('');
          }}
          onSaved={handleRedacted}
        />
      )}
    </div>
  );
};

export default HOAFilesRequest;